import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';

import App from './App';
import { env } from 'env';
import {
  checkIsFetchCategory,
  checkIsTransactionCategory,
  getSentryEnvironment,
  sanitizedBreadcrumb,
  shouldSendFullSentryPii,
} from './helpers/sentry';

import { SentryBreadcrumbCategory } from './interfaces';

let integrations: any = [];
const enableLogs = !['local', 'prod', 'stg'].includes(getSentryEnvironment());
if (!getSentryEnvironment().includes('local')) {
  console.log('Sentry Enabled');

  if (getSentryEnvironment().includes('prod')) {
    const maskSentryReplayMedia =
      env.REACT_APP_SENTRY_REPLAY_MASK_MEDIA === 'true' ? true : false;
    integrations = [
      Sentry.browserTracingIntegration(),
      Sentry.breadcrumbsIntegration({ dom: false, console: true }),
      Sentry.replayIntegration({
        maskAllText: maskSentryReplayMedia,
        maskAllInputs: maskSentryReplayMedia,
        blockAllMedia: maskSentryReplayMedia,
        networkDetailAllowUrls: [
          window.location.origin,
          'amazonaws.com',
          'mytaverse.com',
        ],
      }),
      Sentry.replayCanvasIntegration(),
      Sentry.consoleLoggingIntegration({
        levels: ['log', 'warn', 'error', 'info', 'debug'],
      }),
    ];
  } else {
    integrations = [
      Sentry.browserTracingIntegration(),
      Sentry.breadcrumbsIntegration({ dom: false, console: true }),
      Sentry.consoleLoggingIntegration({
        levels: ['log', 'warn', 'error', 'info', 'debug'],
      }),
    ];
  }

  Sentry.init({
    dsn: env.REACT_APP_SENTRY_DSN,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: Number(
      env.REACT_APP_SENTRY_REPLAY_SESSION_RATE || 0.1,
    ),
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: Number(
      env.REACT_APP_SENTRY_REPLAY_ERROR_RATE || 1.0,
    ),

    environment: getSentryEnvironment(),
    // Non-prod only: attach the request IP (→ geo) and request context to
    // every event for easier debugging. OFF for prod.
    sendDefaultPii: shouldSendFullSentryPii(),
    integrations: integrations,
    beforeBreadcrumb(breadcrumb, hint) {
      if (!breadcrumb.category || !hint) {
        return breadcrumb;
      }

      const category = breadcrumb.category as SentryBreadcrumbCategory;

      if (
        checkIsTransactionCategory(category) ||
        checkIsFetchCategory(category)
      ) {
        return null;
      }

      if (breadcrumb.category === 'xhr' && breadcrumb.data) {
        const url = breadcrumb.data.url;

        const excludeXRHUrls = [
          'https://log-external-gateway.zoom.us/meeting-external',
        ];

        if (
          url &&
          (url.endsWith('.amazonaws.com/ping') ||
            url.endsWith('/connections') ||
            url.endsWith('/api/v1/analytics') ||
            excludeXRHUrls.includes(url))
        ) {
          return null;
        }
      }

      const updatedBreadcrumb = sanitizedBreadcrumb(breadcrumb, hint);

      return updatedBreadcrumb;
    },
    beforeSendLog: (log) => {
      const sentryTags = Sentry.getCurrentScope().getScopeData().tags;

      log.attributes = {
        ...(log.attributes || {}),
        sessionId: sentryTags['sessionId'] || '',
      };

      return log;
    },
    tracesSampleRate: 1.0,
    enableLogs,
  });
} else {
  console.log('Sentry Disabled');
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(<App />);
